<template>
  <v-dialog
    v-model="dialog"
    width="auto"
    data-testid="usage-trends-popup"
  >
    <v-card class="card br-5">
      <v-container>
        <!-- Header -->
        <v-row class="pl-5">
          <v-col class="d-flex align-center">
            <p class="table-title">
              Usage Trend for {{ mapName }}
            </p>
          </v-col>
          <v-col class="d-flex align-center justify-end">
            <TabToggle
              v-model="chartSelect"
              :options="chartTypeOptions"
              :disabled="isLoadingTrends"
              class="mr-2"
            />
            <v-btn
              variant="text"
              icon="mdi-close"
              @click="dialog = false"
            />
          </v-col>
        </v-row>
        <!-- Dropdowns -->
        <v-row class="pl-5">
          <v-col cols="3">
            <v-select
              v-model="segmentSelect"
              density="compact"
              label="Segment"
              :items="segmentItems"
              hide-details
            />
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="typeSelect"
              density="compact"
              label="Type"
              :items="typeItems"
              hide-details
            />
          </v-col>
          <v-col cols="3">
            <v-select
              v-model="timeFrameSelect"
              density="compact"
              label="Time Frame"
              :items="timeFrameItems"
              hide-details
            />
          </v-col>
        </v-row>
        <!-- Subtitle -->
        <v-row class="pl-5">
          <v-col class="d-flex align-center">
            {{ subtitle }}
            <v-tooltip
              location="top"
              text="every user entry to the shared map counts as 1 view"
            >
              <template #activator="{ props }">
                <v-icon
                  class="ml-1"
                  size="small"
                  v-bind="props"
                >
                  mdi-information
                </v-icon>
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
        <!-- Chart -->
        <v-row>
          <v-col>
            <v-skeleton-loader
              v-if="isLoadingTrends"
              type="table-tbody"
              :loading="isLoadingTrends"
            />
            <ag-charts
              v-else-if="chartOptions?.data?.length"
              :options="chartOptions"
            />
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
// 3rd party
import dayjs from 'dayjs';
import {mapActions} from 'vuex';
import {AgChartOptions} from 'ag-charts-community';

// Services and Helpers
import {getDocument} from '@/services/FirestoreService';
import {backendErrorMessage, queryAuditLogs} from '@/services/BackendService';

// Components
import TabToggle from '@/components/Reusable/TabToggle.vue';
import {AgCharts} from 'ag-charts-vue3';
import {getDatesInRange} from '@/lib/ReusableFunctions/Date';

type AuditRow = {
  eventType: string;
  eventDate: {
    value: string;
  };
  userId: string;
};

export default {
  name: 'UsageTrends',
  components: {
    AgCharts,
    TabToggle,
  },
  props: {
    mapId: {
      type: String,
      required: true,
    },
    mapName: {
      type: String,
      required: true,
    },
  },
  emits: ['closed'],
  data() {
    const chartTypeOptions = [
      {value: 'bar', icon: 'mdi-chart-bar'},
      {value: 'line', icon: 'mdi-chart-line'},
      {value: 'area', icon: 'mdi-chart-areaspline-variant'},
    ];
    const segmentItems = ['NorthPoint', 'External', 'All'];
    const timeFrameItems = ['Last 7 days', 'Last 14 days', 'Last 30 days', 'Last 90 days', 'All Time'];
    return {
      isLoadingTrends: true,
      dialog: true,
      chartSelect: chartTypeOptions[0].value,
      chartTypeOptions,
      segmentItems,
      segmentSelect: segmentItems[0],
      typeItems: [] as string[],
      typeSelect: '',
      timeFrameItems,
      timeFrameSelect: timeFrameItems[1],
      chartData: null,
      relevantData: [] as AuditRow[],
      today: new Date(),
      metricNameMap: {} as Record<string, string>,
    };
  },
  computed: {
    startDate() {
      const daysBefore = Number(this.timeFrameSelect.replace(/[^0-9]/g, '')); // if a number isn't found it is cast to 0
      if (daysBefore === 0) return null; // return a date well before we started saving data to bigquery
      return new Date(this.today.getTime() - daysBefore * 24 * 60 * 60 * 1000);
    },
    subtitle() {
      // Total events
      const totalEvents = this.chartOptions?.data?.reduce((acc, obj) => acc += obj.count, 0) ?? 0;
      const prefix = `${totalEvents} total events`;

      // Date range
      const options: Intl.DateTimeFormatOptions = {month: 'short', day: 'numeric', year: 'numeric'};
      let suffix;
      if (this.startDate) {
        suffix = `for ${this.startDate.toLocaleDateString('en-US', options)} - ${this.today.toLocaleDateString('en-US', options)}`;
      } else {
        suffix = `all time`;
      }
      return `${prefix} ${suffix}`;
    },
    chartOptions() {
      if (this.isLoadingTrends) return null;
      return this.getChartOptions();
    },
  },
  watch: {
    dialog(newValue) {
      if (!newValue) {
        this.$emit('closed');
      }
    },
  },
  mounted() {
    this.loadMetricNameMap();
    this.loadTrends();
  },
  methods: {
    ...mapActions(['addErrorMessage']),
    async loadMetricNameMap() {
      try {
        const doc = await getDocument('app-config', 'usageTrends');
        this.metricNameMap = doc.metricNameMap;
      } catch (error) {
        console.error('Error loading metric name map:', error);
      }
    },
    async loadTrends() {
      // set loading to true when starting loading
      this.isLoadingTrends = true;

      let json: AuditRow[];
      try {
        json = await queryAuditLogs<AuditRow>(this.mapId, 'usageStatistics');
      } catch (error) {
        this.addErrorMessage(`Failed to fetch usage statistics, ${backendErrorMessage(error)}`);
        return;
      }
      // get only events that we care about
      this.relevantData = json
        .filter((usage: AuditRow) => {
          // We only care about metrics that are in the metricNameMap
          return usage.eventType === 'editModeAccess'
            || usage.eventType === 'viewModeAccess'
            || usage.eventType.startsWith('layerOn');
        })
        .map((usage: AuditRow) => ({
          // Update the event type with the formatted names
          ...usage,
          eventType: this.metricNameMap[usage.eventType] ?? usage.eventType,
        }));

      // Populate the type dropdown
      const eventTypes = this.relevantData.map((usage) => usage.eventType);
      const eventTypesSet = new Set(eventTypes);
      this.typeItems = Array.from(eventTypesSet);
      this.typeSelect = this.typeItems[0];

      // set loading to false when done loading
      this.isLoadingTrends = false;
    },
    getChartOptions() : AgChartOptions {
      // filter by segment
      let segmentFiltered;
      if (this.segmentSelect === 'NorthPoint') {
        segmentFiltered = this.relevantData.filter((usage) => usage.userId !== 'anonymous');
      } else if (this.segmentSelect === 'External') {
        segmentFiltered = this.relevantData.filter((usage) => usage.userId === 'anonymous');
      } else {
        segmentFiltered = this.relevantData;
      }

      // filter by type
      const typeFiltered = segmentFiltered.filter((usage) => usage.eventType === this.typeSelect);

      // filter by date
      let dateFiltered;
      if (this.startDate) {
        const startTime = this.startDate.getTime();
        const todayTime = this.today.getTime();
        dateFiltered = typeFiltered.filter((usage) => {
          const eventTime = new Date(usage.eventDate.value).getTime();
          return (eventTime <= todayTime && eventTime >= startTime);
        });
      } else {
        dateFiltered = typeFiltered;
      }

      const reduced = dateFiltered.reduce((accumulator: {date: string; count: number}[], currentObject: AuditRow) => {
        // Parse date and ignore time
        const fullDate = new Date(currentObject.eventDate.value);
        const parsedDate = fullDate.getFullYear() + '-' + (fullDate.getMonth() + 1).toString().padStart(2, '0') + '-' + fullDate.getDate().toString().padStart(2, '0');
        if (!accumulator.some((item) => item.date === parsedDate)) {
          accumulator.push({date: parsedDate, count: 1});
        } else {
          const index = accumulator.findIndex((item) => item.date === parsedDate);
          accumulator[index].count += 1;
        }
        return accumulator;
      }, []);

      // Sort by date
      reduced.sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      });

      let data: {date: string; count: number}[];
      if (!reduced.length) {
        data = [];
      } else {
        const dates = getDatesInRange(reduced[0].date, reduced[reduced.length - 1].date);
        // Fill in missing dates with 0 count
        data = dates.map((date) => {
          const found = reduced.find((item) => item.date === date);
          return found ? found : {date, count: 0};
        });
      }

      // Series
      const series = [{
        type: this.chartSelect,
        xKey: 'date',
        yKey: 'count',
      }];

      // Axes
      const axes = [
        {
          position: 'bottom',
          type: 'category',
          label: {formatter: (params: {value: string}) => dayjs(params.value).format('MMM D')},
        },
        {
          position: 'left',
          type: 'number',
          min: 0,
          max: data.length ? Math.max(...data.map((item) => item.count)) + 1 : 1,
          interval: {step: 1},
          label: {format: '.0f'},
        },
      ];

      // @ts-ignore AgChartOptions does not properly check for the correct series and axes types
      return {data, series, axes};
    },
  },
};
</script>

<style scoped lang="scss">
.card {
  width: 1000px;
  @include main.custom-width(1000, auto);
}

.table-title {
  font-size: 18px;
  font-weight: 500;
  color: main.$npGray;
}

</style>
